/**
 * Selection V2 Types
 *
 * Type definitions for the new Filters + Rank & Pick selection system
 */

export type Comparator = '>' | '>=' | '<' | '<=' | '==' | '!=' | 'between';
export type MatchMode = 'ALL' | 'ANY';
export type Order = 'asc' | 'desc';
export type Method = 'top_pct' | 'top_n' | 'bottom_pct' | 'bottom_n';
export type NAPolicy = 'drop' | 'worst';
export type PartitionBy = 'sector' | 'industry' | null;

export interface FilterRule {
  id: string;
  left: string;           // DSL metric/formula (e.g., "eps.ttm(0)")
  comparator: Comparator;
  right?: number;
  right2?: number;        // for "between"
  naPolicy?: NAPolicy;    // default 'drop'
}

export interface FilterGroup {
  id: string;
  match: MatchMode;       // ALL=AND, ANY=OR (within group)
  rules: FilterRule[];
}

export interface FiltersBlock {
  groups: FilterGroup[];  // AND across groups
}

export interface Tie {
  id: string;
  field: string;          // DSL or special fields like "ticker"
  order: Order;
}

export interface RankPickSpec {
  formula: string;
  order: Order;
  method: Method;
  k: number;
  naPolicy?: NAPolicy;    // default 'drop'
  ties?: Tie[];
  partitionBy?: PartitionBy;
  maxPerBucket?: number | null;
}

export interface SelectionV2 {
  filters: FiltersBlock;
  rankPick: RankPickSpec;
}

// Preview API response types
export interface PreviewResult {
  status: string;
  universeCount: number;
  survivorCount: number;
  pickedCount: number;
  droppedNA: number;
  topSample: string[];
  explanations: string[];
}

// Default values
export const DEFAULT_FILTER_RULE: Omit<FilterRule, 'id'> = {
  left: '',
  comparator: '>',
  right: 0,
  naPolicy: 'drop'
};

export const DEFAULT_FILTER_GROUP: Omit<FilterGroup, 'id'> = {
  match: 'ALL',
  rules: []
};

export const DEFAULT_RANK_PICK: RankPickSpec = {
  formula: '',
  order: 'desc',
  method: 'top_n',
  k: 20,
  naPolicy: 'drop',
  ties: [],
  partitionBy: null,
  maxPerBucket: null
};

export const DEFAULT_SELECTION_V2: SelectionV2 = {
  filters: {
    groups: []
  },
  rankPick: DEFAULT_RANK_PICK
};

// Helper functions
export function createFilterRule(partial?: Partial<FilterRule>): FilterRule {
  return {
    id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
    ...DEFAULT_FILTER_RULE,
    ...partial
  };
}

export function createFilterGroup(partial?: Partial<FilterGroup>): FilterGroup {
  return {
    id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
    ...DEFAULT_FILTER_GROUP,
    rules: [],
    ...partial
  };
}

export function createTie(partial?: Partial<Tie>): Tie {
  return {
    id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
    field: '',
    order: 'asc',
    ...partial
  };
}
